import { defineCollection } from "@rebasepro/common";
import prospectsCollection from "./prospects.js";
import companiesCollection from "./companies.js";
import mailboxesCollection from "./mailboxes.js";
/**
 * Email threads — one conversation with one set of people.
 *
 * ── Why a conversation and not a message ─────────────────────────────────────
 * The unit anyone actually works in. "Have we heard back from Acme" is a
 * question about a conversation, not about a message, and the link to the rest
 * of this database belongs here for the same reason: what a conversation is
 * about is a property of the conversation, not of any one message in it.
 *
 * This replaces `replies`, which held inbound mail that could be matched to our
 * own outreach and nothing else. That table could answer "did this campaign get
 * an answer" and could not answer "what has this person said to us", which is
 * the question a CRM exists for. A mailbox is not a list of responses to us.
 *
 * ── The link ─────────────────────────────────────────────────────────────────
 * A thread points at a person and/or the company they work for, and neither is
 * required: a mailbox receives from strangers, and "we do not know who this is
 * yet" is a real and common state rather than a gap to be filled in.
 *
 * `linked_by` is what keeps the guess and the correction from fighting. Ingest
 * sets `auto` from the sender's address and domain; a person choosing the right
 * company writes `manual`, and ingest never touches a `manual` thread again.
 * Without it, a thread somebody moved onto the right company moves back on the
 * next poll, which teaches people to stop correcting it.
 *
 * ── Before the link: is it a conversation at all ─────────────────────────────
 * `kind` says whether there is anybody on the other end. A quarter of a real
 * mailbox is machines — invoices, calendar RSVPs, delivery failures — and those
 * threads are kept, readable, and excluded from every queue: nothing is owed to
 * them and nobody can be assigned to them. Decided at ingest by
 * `backend/src/agent/inbox/kind.ts`; changing it by hand writes a rule in
 * `email_sender_rules` so the next thread from that sender is born right.
 *
 * Headers only catch the mail that admits to being automated. Cold pitches,
 * invoices from people and SaaS mail from `maria@` all look like
 * correspondence, so a thread the headers call a person's is screened once more
 * by `backend/src/agent/inbox/triage.ts`. Anything that is not a conversation
 * becomes `automated` with a `category` and a `filed_reason`.
 */
const emailThreadsCollection = defineCollection({
    name: "Conversations",
    singularName: "Conversation",
    slug: "email_threads",
    table: "email_threads",
    description: "One conversation with one set of people, in both directions. The unit the inbox works in.",
    properties: {
        id: { name: "ID", type: "string", isId: "uuid", admin: { readOnly: true } },
        subject: { name: "Subject", type: "string" },
        subject_key: {
            name: "Subject key",
            type: "string",
            description: "The subject with 'Re:' and accents removed, used to join a reply that carries no References header. Derived — do not edit, and see backend/src/agent/inbox/threading.ts before changing how it is built: existing conversations split if the rule moves.",
            admin: { readOnly: true }
        },
        participants: {
            name: "Participants",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Address" },
            description: "Every address that has appeared in this conversation, ours excluded",
            admin: { readOnly: true }
        },
        counterpart_name: {
            name: "Person",
            type: "string",
            description: "The name on the other side, as their mail client spells it today",
            admin: { readOnly: true }
        },
        kind: {
            name: "Kind",
            type: "string",
            defaultValue: "person",
            enum: [
                { id: "person", label: "Conversation", color: "green" },
                { id: "automated", label: "Not a conversation", color: "gray" },
                { id: "broadcast", label: "Sent to nobody in particular", color: "gray" }
            ],
            description: "Only a conversation with a person can owe a reply or be linked to a prospect. Everything else (machines, pitches, invoices) is kept and not counted."
        },
        category: {
            name: "What it is",
            type: "string",
            enum: [
                { id: "conversation", label: "Conversation", color: "green" },
                { id: "pitch", label: "Cold pitch", color: "orange" },
                { id: "transactional", label: "Invoice, receipt or account", color: "blue" },
                { id: "notification", label: "Notification", color: "gray" },
                { id: "newsletter", label: "Newsletter or marketing", color: "purple" },
                { id: "spam", label: "Spam", color: "red" }
            ],
            description: "The screen's verdict. Empty when the headers already said a machine wrote it, or before it was screened.",
            admin: { readOnly: true }
        },
        filed_reason: {
            name: "Why",
            type: "string",
            description: "One line on why it is, or is not, a conversation: 'Hetzner invoice for September', 'SEO agency offering backlinks', 'It has an unsubscribe link'.",
            admin: { readOnly: true }
        },
        triaged_by: {
            name: "Decided by",
            type: "string",
            enum: [
                { id: "model", label: "The screen", color: "orange" },
                { id: "history", label: "We wrote in it", color: "green" },
                { id: "rule", label: "A sender rule", color: "blue" },
                { id: "headers", label: "The message's headers", color: "gray" },
                { id: "person", label: "Somebody here", color: "green" }
            ],
            admin: { readOnly: true }
        },
        triaged_at: {
            name: "Screened",
            type: "date",
            description: "Empty means not screened yet. Unscreened conversations stay in the queues, so a broken screen never hides anybody.",
            admin: { readOnly: true }
        },
        mailbox: {
            name: "Mailbox",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => mailboxesCollection,
                relationName: "mailbox",
                localKey: "mailbox_id",
                onDelete: "set null"
            },
            description: "The inbox it arrived in, and so the address a reply goes out from"
        },
        prospect: {
            name: "Person",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => prospectsCollection,
                relationName: "prospect",
                localKey: "prospect_id",
                // A conversation outlives the card it was linked to: deleting a
                // prospect must not delete the evidence of what was said.
                onDelete: "set null"
            }
        },
        company: {
            name: "Company",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => companiesCollection,
                relationName: "company",
                localKey: "company_id",
                onDelete: "set null"
            }
        },
        linked_by: {
            name: "Linked by",
            type: "string",
            defaultValue: "auto",
            enum: [
                { id: "auto", label: "The sync worked it out", color: "gray" },
                { id: "manual", label: "By hand", color: "green" }
            ],
            description: "'By hand' always wins: the sync never re-links a conversation somebody assigned themselves."
        },
        status: {
            name: "Status",
            type: "string",
            defaultValue: "open",
            enum: [
                { id: "open", label: "Open", color: "blue" },
                { id: "closed", label: "Closed", color: "gray" }
            ]
        },
        needs_reply: {
            name: "Owes a reply",
            type: "boolean",
            defaultValue: true,
            description: "Whether their last message actually asks for an answer. A 'thanks' does not — see backend/src/agent/inbox/reply.ts."
        },
        unread: { name: "Unread", type: "boolean", defaultValue: false },
        follow_up_at: {
            name: "Look again on",
            type: "date",
            description: "When to pick this up again. Cleared by itself when they write back."
        },
        last_snippet: {
            name: "Preview",
            type: "string",
            description: "The first words of the newest message, so the list does not need a query per row",
            admin: { readOnly: true }
        },
        message_count: { name: "Messages", type: "number", defaultValue: 0, admin: { readOnly: true } },
        last_message_at: { name: "Last message", type: "date", admin: { readOnly: true } },
        last_direction: {
            name: "Last message was",
            type: "string",
            enum: [
                { id: "inbound", label: "Received", color: "green" },
                { id: "outbound", label: "Sent", color: "blue" }
            ],
            admin: { readOnly: true }
        },
        last_inbound_at: {
            name: "They last wrote",
            type: "date",
            description: "Kept apart from `last_message_at` on purpose: 'when did they answer' is the date that decides whether a follow-up is owed.",
            admin: { readOnly: true }
        },
        last_outbound_at: { name: "We last wrote", type: "date", admin: { readOnly: true } },
        gmail_thread_id: {
            name: "Gmail conversation",
            type: "string",
            description: "X-GM-THRID. What makes this panel and Gmail group the same messages the same way.",
            admin: { readOnly: true }
        },
        created_at: { name: "Created", type: "date", autoValue: "on_create", admin: { readOnly: true } },
        updated_at: { name: "Updated", type: "date", autoValue: "on_update", admin: { readOnly: true } }
    },
    admin: {
        icon: "Forum",
        group: "Outreach",
        defaultViewMode: "list",
        enabledViews: ["list", "table"],
        /**
         * The title is the person, not the subject.
         *
         * "Have we heard back from Acme" is a question about who, and a column
         * of subject lines answers it slowly — half of them are "Re: Re:
         * something" and several are the same words from different people. The
         * subject moves into the subtitle beside the preview, which is where a
         * mail client puts it too.
         */
        display: {
            title: ({ entity }) => {
                const name = typeof entity.values.counterpart_name === "string" ? entity.values.counterpart_name.trim() : "";
                const participants = entity.values.participants;
                return name || (Array.isArray(participants) ? String(participants[0] ?? "") : "") || "(nobody)";
            },
            subtitle: ({ entity }) => [entity.values.subject, entity.values.last_snippet]
                .map(value => (typeof value === "string" ? value.trim() : ""))
                .filter(Boolean)
                .join(" — "),
            date: "last_message_at",
            tags: ({ entity }) => [
                entity.values.needs_reply && entity.values.kind === "person" ? "owes a reply" : "",
                entity.values.unread ? "unread" : "",
                entity.values.kind === "automated" ? (typeof entity.values.category === "string" ? entity.values.category : "machine") : "",
                entity.values.message_count ? `${entity.values.message_count} msg` : ""
            ].filter(Boolean)
        },
        sort: ["last_message_at", "desc"],
        filterPresets: [
            {
                label: "Owes a reply",
                filterValues: { kind: ["==", "person"], needs_reply: ["==", true], status: ["==", "open"] }
            },
            { label: "Unread", filterValues: { unread: ["==", true] } },
            {
                label: "Nobody linked yet",
                filterValues: { kind: ["==", "person"], linked_by: ["==", "auto"] }
            },
            { label: "Filtered out", filterValues: { kind: ["==", "automated"] } }
        ],
        propertiesOrder: [
            "subject",
            "mailbox",
            "counterpart_name",
            "last_snippet",
            "kind",
            "category",
            "filed_reason",
            "needs_reply",
            "unread",
            "prospect",
            "company",
            "last_message_at",
            "last_direction",
            "message_count",
            "status",
            "follow_up_at",
            "linked_by",
            "participants",
            "last_inbound_at",
            "last_outbound_at",
            "triaged_by",
            "triaged_at",
            "subject_key",
            "gmail_thread_id",
            "created_at",
            "updated_at"
        ]
    }
});
export default emailThreadsCollection;
